import { createFileRoute } from "@tanstack/react-router";
import { AdminShell } from "@/components/admin/AdminShell";
import { useAdminAppointments } from "@/hooks/useAdminAppointments";
import { formatISTDateTime } from "@/lib/date/ist";
import { Badge } from "@/components/ui/badge";
import { Loader2, ArrowLeft, Save } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { toast } from "sonner";

export const Route = createFileRoute("/_admin/admin/appointments/$appointmentId")({
  component: AdminAppointmentDetailsPage,
});

function AdminAppointmentDetailsPage() {
  const { appointmentId } = Route.useParams();
  const { appointments, updateStatus, saveNotes, isLoading } = useAdminAppointments();
  const apt = appointments.find(a => a.id === appointmentId);

  const [status, setStatus] = useState<string>("");
  const [adminNotes, setAdminNotes] = useState<string>("");
  const [privateNotes, setPrivateNotes] = useState<string>("");
  const [recommendations, setRecommendations] = useState<string>("");

  useEffect(() => {
    if (apt) {
      setStatus(apt.status);
      setAdminNotes(apt.admin_notes || "");
      // Note: notes are fetched in a separate query in a full implementation, 
      // but for MVP we assume notes might be present if the join is expanded or use placeholder
    }
  }, [apt]);

  if (isLoading) {
    return (
      <AdminShell>
        <div className="flex h-60 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AdminShell>
    );
  }

  if (!apt) {
    return (
      <AdminShell>
        <div className="p-8 text-center text-muted-foreground">Appointment not found.</div>
      </AdminShell>
    );
  }

  const handleUpdateStatus = async () => {
    try {
      await updateStatus.mutateAsync({ 
        appointmentId: apt.id, 
        status: status as any,
        adminNotes 
      });
      toast.success("Appointment status updated");
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const handleSaveNotes = async () => {
    try {
      await saveNotes.mutateAsync({
        appointmentId: apt.id,
        privateNotes,
        customerRecommendations: recommendations
      });
      toast.success("Consultation notes saved");
    } catch (e) {
      toast.error("Failed to save notes");
    }
  };

  return (
    <AdminShell>
      <div className="flex flex-col gap-6">
        <header>
          <Link
            to="/admin/appointments"
            className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Calendar
          </Link>
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-semibold tracking-tight">{apt.reference}</h1>
            <Badge className="uppercase">{apt.status}</Badge>
          </div>
        </header>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <section className="rounded-xl border border-border bg-card p-6">
              <h2 className="mb-4 text-lg font-medium">Consultation Info</h2>
              <dl className="grid gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground">Customer</dt>
                  <dd className="mt-1">
                    <div className="font-medium">{(apt.customer_profiles as any)?.full_name}</div>
                    <div className="text-xs text-muted-foreground">{(apt.customer_profiles as any)?.phone}</div>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground">Date & Time</dt>
                  <dd className="mt-1 font-medium">{formatISTDateTime(apt.start_time)}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground">Type</dt>
                  <dd className="mt-1 font-medium">{(apt.consultation_types as any)?.name}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground">Consultant</dt>
                  <dd className="mt-1 font-medium">{(apt.consultants as any)?.name}</dd>
                </div>
              </dl>
              {apt.customer_notes && (
                <div className="mt-6 border-t pt-4">
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground">Customer Notes</dt>
                  <dd className="mt-1 text-sm italic">"{apt.customer_notes}"</dd>
                </div>
              )}
            </section>

            <section className="rounded-xl border border-border bg-card p-6">
              <h2 className="mb-4 text-lg font-medium">Consultation Notes & Recommendations</h2>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="recommendations">Customer Recommendations (Visible to Customer)</Label>
                  <Textarea
                    id="recommendations"
                    placeholder="E.g. increase protein intake, avoid late-night snacks..."
                    className="min-h-[120px]"
                    value={recommendations}
                    onChange={(e) => setRecommendations(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="privateNotes">Private Consultant Notes (Internal Only)</Label>
                  <Textarea
                    id="privateNotes"
                    placeholder="Enter private observations here..."
                    className="min-h-[100px]"
                    value={privateNotes}
                    onChange={(e) => setPrivateNotes(e.target.value)}
                  />
                </div>
                <Button 
                  onClick={handleSaveNotes} 
                  disabled={saveNotes.isPending}
                  className="gap-2"
                >
                  <Save className="h-4 w-4" />
                  Save Notes
                </Button>
              </div>
            </section>
          </div>

          <div className="space-y-6">
            <section className="rounded-xl border border-border bg-card p-6">
              <h2 className="mb-4 text-lg font-medium">Status & Admin Notes</h2>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="no_show">No Show</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="adminNotes">Operational Notes</Label>
                  <Textarea
                    id="adminNotes"
                    placeholder="E.g. reschedule requested..."
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                  />
                </div>
                <Button 
                  variant="secondary" 
                  className="w-full"
                  onClick={handleUpdateStatus}
                  disabled={updateStatus.isPending}
                >
                  Update Status
                </Button>
              </div>
            </section>
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
